import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
    Leaf, ArrowLeft, Lock, Coins, MapPin
} from '@phosphor-icons/react';

export default function HabitatsPage() {
    const { user, api } = useAuth();
    const [habitats, setHabitats] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api().get('/habitats');
                setHabitats(res.data);
            } catch (err) {
                console.error('Failed to load habitats');
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sort: unlocked first (free first), then by unlock cost
    const sorted = [...habitats].sort((a, b) => {
        if (a.unlocked && !b.unlocked) return -1;
        if (!a.unlocked && b.unlocked) return 1;
        return a.unlock_cost - b.unlock_cost;
    });

    if (loading) {
        return (
            <div className="min-h-screen bg-[#fefce8] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#fefce8] bg-[radial-gradient(#cbd5e1_1.5px,transparent_1.5px)] [background-size:24px_24px]">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#0f172a]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#0f172a] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#0f172a] hidden sm:block">
                                Habitats
                            </span>
                        </Link>
                        <Link to="/dashboard" className="bg-white text-[#0f172a] border-4 border-[#0f172a] shadow-[4px_4px_0px_#0f172a] rounded-xl font-bold px-4 py-2 text-sm flex items-center gap-1.5 hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_#0f172a] transition-all" data-testid="back-dashboard">
                            <ArrowLeft weight="bold" size={16} /> Dashboard
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-300 border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-3xl p-8 mb-10 relative overflow-hidden"
                >
                    <div className="z-10 relative">
                        <h1 className="font-fredoka text-3xl md:text-4xl font-bold text-[#0f172a]">Explore Australian Habitats</h1>
                        <p className="text-lg text-[#0f172a]/70 font-bold mt-2">
                            Discover 12 unique ecosystems across Australia — from alpine peaks to coral reefs
                        </p>
                        {user?.role === 'child' && (
                            <div className="flex items-center gap-2 mt-3 text-[#0f172a]/60">
                                <Coins weight="bold" size={18} />
                                <span className="text-sm font-bold">Your tokens: {user?.xp || 0} — earn more to unlock new habitats!</span>
                            </div>
                        )}
                    </div>
                    <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-green-400 rounded-full border-4 border-[#0f172a] opacity-40 z-0" />
                </motion.div>

                {/* Habitat Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sorted.map((habitat, i) => {
                        const isLocked = !habitat.unlocked;
                        const theme = habitat.theme || {};

                        return (
                            <motion.div
                                key={habitat.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04 }}
                            >
                                {isLocked ? (
                                    <div
                                        className="relative border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl overflow-hidden opacity-60"
                                        data-testid={`habitat-card-${habitat.id}`}
                                    >
                                        {/* Locked Image */}
                                        <div className="h-44 bg-gray-200 relative">
                                            <img
                                                src={habitat.image_url}
                                                alt={habitat.name}
                                                crossOrigin="anonymous"
                                                referrerPolicy="no-referrer"
                                                className="w-full h-full object-cover grayscale"
                                            />
                                            <div className="absolute inset-0 bg-[#0f172a]/50 flex items-center justify-center">
                                                <div className="flex items-center gap-2 bg-[#0f172a] text-white px-4 py-2 rounded-xl font-bold text-sm border-2 border-white/20">
                                                    <Lock weight="bold" size={16} />
                                                    {habitat.unlock_cost} Tokens
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-4 bg-white">
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">{habitat.emoji}</span>
                                                <h3 className="font-fredoka text-lg font-bold text-[#0f172a]">{habitat.name}</h3>
                                            </div>
                                            <p className="text-sm text-[#475569] mt-1 line-clamp-2">{habitat.description}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <Link to={`/habitats/${habitat.id}`} data-testid={`habitat-card-${habitat.id}`}>
                                        <div
                                            className="relative border-4 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] rounded-2xl overflow-hidden hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[8px_8px_0px_#0f172a] transition-all group"
                                        >
                                            {/* Image */}
                                            <div className="h-44 relative overflow-hidden">
                                                <img
                                                    src={habitat.image_url}
                                                    alt={habitat.name}
                                                    crossOrigin="anonymous"
                                                    referrerPolicy="no-referrer"
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                                {/* Location count badge */}
                                                <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-white/90 px-2 py-1 rounded-lg text-xs font-bold text-[#0f172a]">
                                                    <MapPin weight="bold" size={12} />
                                                    {habitat.locations?.length || 0} parks
                                                </div>
                                                {habitat.unlock_cost === 0 && (
                                                    <div className="absolute top-2 left-2 bg-[#22c55e] text-white px-2 py-0.5 rounded-lg text-xs font-bold border-2 border-[#0f172a]">
                                                        Free
                                                    </div>
                                                )}
                                            </div>
                                            {/* Content */}
                                            <div className="p-4" style={{ backgroundColor: theme.card_bg || '#fff' }}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-2xl">{habitat.emoji}</span>
                                                    <h3 className="font-fredoka text-lg font-bold" style={{ color: theme.text || '#0f172a' }}>{habitat.name}</h3>
                                                </div>
                                                <p className="text-sm mt-1 line-clamp-2" style={{ color: `${theme.text || '#475569'}99` }}>{habitat.description}</p>
                                            </div>
                                        </div>
                                    </Link>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
